import { useState, useEffect, useCallback } from 'react';
import { Table, Button, message, Modal, Input, Breadcrumb, DatePicker, Space, Select, Form, Descriptions } from 'antd';
import { HomeOutlined, PlusOutlined, DownloadOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../api';
import dayjs from 'dayjs';

const { TextArea } = Input;

const InterviewManagementPage = () => {
  const [allInterviews, setAllInterviews] = useState([]);
  const [filteredInterviews, setFilteredInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState([dayjs().subtract(1, 'month'), dayjs().add(1, 'month')]);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [classFilter, setClassFilter] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentSearchText, setStudentSearchText] = useState('');

  const fetchAllInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/interview/all', { params: { page: 1, pageSize: 1000 } });
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.records)
          ? response.data.records
          : [];
      setAllInterviews(data);
    } catch (error) {
      message.error('获取约谈记录失败');
    }
    setLoading(false);
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      const response = await api.get('/users/students');
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('获取学生列表失败:', error);
    }
  }, []);

  // 综合筛选函数
  const applyFilters = useCallback((data, dates, search, classVal) => {
    let filtered = data;

    // 日期范围筛选
    if (dates && dates.length === 2) {
      const startStr = dates[0].format('YYYY-MM-DD');
      const endStr = dates[1].format('YYYY-MM-DD');
      filtered = filtered.filter(r => {
        const interviewDate = r.interviewTime?.split('T')[0];
        return interviewDate >= startStr && interviewDate <= endStr;
      });
    }

    // 搜索筛选（姓名）
    if (search && search.trim()) {
      const keyword = search.trim().toLowerCase();
      filtered = filtered.filter(r => {
        const name = (r.student?.name || '').toLowerCase();
        return name.includes(keyword);
      });
    }

    // 班级筛选
    if (classVal) {
      filtered = filtered.filter(r => r.student?.className === classVal);
    }

    setFilteredInterviews(filtered);
    setSelectedRowKeys([]);
  }, []);

  useEffect(() => {
    fetchAllInterviews();
    fetchStudents();
  }, [fetchAllInterviews, fetchStudents]);

  useEffect(() => {
    applyFilters(allInterviews, dateRange, searchText, classFilter);
  }, [applyFilters, allInterviews, dateRange, searchText, classFilter]);

  // 获取班级列表
  const getClassList = () => {
    const classes = [...new Set(allInterviews.map(r => r.student?.className).filter(Boolean))];
    return classes.map(c => ({ label: c, value: c }));
  };

  // 获取学生选项（支持搜索）
  const getStudentOptions = () => {
    let filtered = students;
    if (studentSearchText) {
      const keyword = studentSearchText.toLowerCase();
      filtered = students.filter(s => 
        s.name?.toLowerCase().includes(keyword) || 
        s.className?.toLowerCase().includes(keyword)
      );
    }
    return filtered.map(s => ({
      label: `${s.name} - ${s.className || '未分班'}`,
      value: s.id
    }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleClassFilter = (value) => {
    setClassFilter(value);
  };

  // 导出Excel
  const handleExport = async () => {
    const hasSelection = selectedRowKeys.length > 0;
    if (!hasSelection && (!dateRange || !dateRange[0] || !dateRange[1])) {
      message.warning('请选择导出的时间范围');
      return;
    }

    try {
      message.loading({ content: '正在生成导出文件...', key: 'exporting' });
      const ids = hasSelection ? selectedRowKeys : [];
      const params = hasSelection
        ? {}
        : {
            startDate: dateRange[0].format('YYYY-MM-DD'),
            endDate: dateRange[1].format('YYYY-MM-DD')
          };
      const response = await api.post('/interview/export', ids, {
        responseType: 'blob',
        params
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const dateStr = dateRange ? `${dateRange[0].format('YYYYMMDD')}-${dateRange[1].format('YYYYMMDD')}` : dayjs().format('YYYYMMDD');
      link.setAttribute('download', `约谈记录_${dateStr}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success({ content: '导出成功', key: 'exporting' });
    } catch (error) {
      console.error(error);
      message.error({ content: '导出失败', key: 'exporting' });
    }
  };

  // 打开新增/编辑弹窗
  const showModal = (record = null) => {
    setEditingRecord(record);
    if (record) {
      form.setFieldsValue({
        studentId: record.studentId,
        interviewTime: dayjs(record.interviewTime),
        content: record.content,
        summary: record.summary,
        remark: record.remark
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        interviewTime: dayjs()
      });
    }
    setIsModalVisible(true);
  };

  // 保存约谈记录
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        studentId: values.studentId,
        interviewTime: values.interviewTime.format('YYYY-MM-DDTHH:mm:ss'),
        content: values.content,
        summary: values.summary,
        remark: values.remark
      };

      if (editingRecord) {
        await api.put(`/interview/${editingRecord.id}`, payload);
        message.success('约谈记录已更新');
      } else {
        await api.post('/interview/create', payload);
        message.success('约谈记录已创建');
      }

      setIsModalVisible(false);
      fetchAllInterviews();
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.errorFields) {
        // 表单验证错误
      } else {
        message.error('操作失败');
      }
    }
  };

  // 删除约谈记录
  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除对 ${record.student?.name || '该学生'} 的约谈记录吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/interview/${record.id}`);
          message.success('删除成功');
          fetchAllInterviews();
        } catch (error) {
          message.error(error.response?.data?.error || '删除失败');
        }
      }
    });
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-';
    return dateTimeStr.replace('T', ' ').substring(0, 16);
  };

  const columns = [
    {
      title: '学生姓名',
      dataIndex: ['student', 'name'],
      key: 'studentName',
      width: 100,
    },
    {
      title: '班级',
      dataIndex: ['student', 'className'],
      key: 'className',
      width: 120,
    },
    {
      title: '约谈时间',
      dataIndex: 'interviewTime',
      key: 'interviewTime',
      width: 160,
      render: (text) => formatDateTime(text),
      sorter: (a, b) => new Date(a.interviewTime) - new Date(b.interviewTime),
      defaultSortOrder: 'descend',
    },
    {
      title: '约谈内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      width: 200,
      render: (text) => text || '-',
    },
    {
      title: '约谈总结',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
      width: 200,
      render: (text) => text || '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: '约谈人',
      dataIndex: 'creatorName',
      key: 'creatorName',
      width: 100,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => setViewingRecord(record)}
            style={{ padding: 0 }}
            aria-label="查看约谈详情"
            title="查看详情"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            style={{ padding: 0 }}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            style={{ padding: 0 }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Breadcrumb items={[
            { title: <><HomeOutlined /> 首页</> },
            { title: '约谈管理' },
          ]} />
          <h2 className="page-title" style={{ margin: '8px 0 0 0' }}>约谈管理</h2>
        </div>
        <Space>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
            导出记录
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            添加约谈
          </Button>
        </Space>
      </div>

      <div className="table-card" style={{ background: '#fff', padding: 16, borderRadius: 8 }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <Space wrap>
            <Space.Compact>
              <Input
                placeholder="搜索学生姓名..."
                allowClear
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  if (!e.target.value) handleSearch('');
                }}
                onPressEnter={() => handleSearch(searchText)}
                style={{ width: 140 }}
                prefix={<SearchOutlined />}
              />
              <Button aria-label="搜索学生" icon={<SearchOutlined />} onClick={() => handleSearch(searchText)} />
            </Space.Compact>
            <Select
              placeholder="所有班级"
              allowClear
              style={{ width: 140 }}
              options={getClassList()}
              value={classFilter}
              onChange={handleClassFilter}
            />
          </Space>
          <Space>
            <span>从</span>
            <DatePicker
              value={dateRange?.[0]}
              onChange={(date) => handleDateRangeChange([date, dateRange?.[1]])}
              allowClear={false}
            />
            <span>至</span>
            <DatePicker
              value={dateRange?.[1]}
              onChange={(date) => handleDateRangeChange([dateRange?.[0], date])}
              allowClear={false}
            />
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredInterviews}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }}
        />
      </div>

      {/* 新增/编辑约谈弹窗 */}
      <Modal
        title={editingRecord ? '编辑约谈记录' : '添加约谈记录'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="interviewTime"
            label="约谈时间"
            rules={[{ required: true, message: '请选择约谈时间' }]}
          >
            <DatePicker showTime format="YYYY/MM/DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="studentId"
            label="选择学生"
            rules={[{ required: true, message: '请选择学生' }]}
            extra="输入学生姓名或班级进行搜索"
          >
            <Select
              showSearch
              placeholder="请选择学生"
              options={getStudentOptions()}
              filterOption={false}
              onSearch={setStudentSearchText}
              disabled={!!editingRecord}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="约谈内容"
            rules={[{ required: true, message: '请输入约谈内容' }]}
          >
            <TextArea rows={3} placeholder="请输入约谈内容" />
          </Form.Item>

          <Form.Item
            name="summary"
            label="约谈总结"
            rules={[{ required: true, message: '请输入约谈总结' }]}
          >
            <TextArea rows={3} placeholder="请输入约谈总结" />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="约谈详情"
        open={!!viewingRecord}
        onCancel={() => setViewingRecord(null)}
        footer={null}
        width={640}
      >
        {viewingRecord && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="学生姓名">{viewingRecord.student?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="班级">{viewingRecord.student?.className || '-'}</Descriptions.Item>
            <Descriptions.Item label="约谈时间">{formatDateTime(viewingRecord.interviewTime)}</Descriptions.Item>
            <Descriptions.Item label="约谈人">{viewingRecord.creatorName || '-'}</Descriptions.Item>
            <Descriptions.Item label="约谈内容" span={2}>{viewingRecord.content || '-'}</Descriptions.Item>
            <Descriptions.Item label="约谈总结" span={2}>{viewingRecord.summary || '-'}</Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>{viewingRecord.remark || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default InterviewManagementPage;
